const FOOTER_SELECTOR = '[data-maurten-footer]';
const TOGGLE_SELECTOR = '[data-maurten-footer-toggle]';
const PANEL_SELECTOR = '[data-maurten-footer-panel]';

const NEWSLETTER_SELECTOR = '[data-maurten-newsletter]';
const NEWSLETTER_TRIGGER_SELECTOR = '[data-maurten-newsletter-trigger]';
const NEWSLETTER_CLOSE_SELECTOR = '[data-maurten-newsletter-close]';

const COUNTRY_DIALOG_SELECTOR = '[data-maurten-country-dialog]';
const COUNTRY_TRIGGER_SELECTOR = '[data-maurten-country-trigger]';
const COUNTRY_CLOSE_SELECTOR = '[data-maurten-country-close]';
const COUNTRY_SELECT_SELECTOR = '[data-maurten-country-select]';

/**
 * @typedef {HTMLElement & {
 *   showDialog?: () => void,
 *   closeDialog?: () => void,
 * }} MaurtenDialogElement
 */

/**
 * @param {string} selector
 * @returns {MaurtenDialogElement | null}
 */
function getDialogComponent(selector) {
  const component = document.querySelector(selector);
  if (!(component instanceof HTMLElement)) return null;

  return /** @type {MaurtenDialogElement} */ (component);
}

/**
 * @returns {MaurtenDialogElement | null}
 */
function getNewsletterComponent() {
  return getDialogComponent(NEWSLETTER_SELECTOR);
}

/**
 * @returns {MaurtenDialogElement | null}
 */
function getCountryDialogComponent() {
  return getDialogComponent(COUNTRY_DIALOG_SELECTOR);
}

function openNewsletter() {
  const component = getNewsletterComponent();
  if (!component) return;

  if (typeof component.showDialog === 'function') {
    component.showDialog();
  } else {
    const dialog = component.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) {
      dialog.showModal();
    }
  }
}

function closeNewsletter() {
  const component = getNewsletterComponent();
  if (!component) return;

  if (typeof component.closeDialog === 'function') {
    component.closeDialog();
  } else {
    const dialog = component.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) {
      dialog.close();
    }
  }
}

/**
 * @param {HTMLElement} component
 */
function focusCountrySelect(component) {
  requestAnimationFrame(() => {
    const select = component.querySelector(COUNTRY_SELECT_SELECTOR);
    if (select instanceof HTMLSelectElement) {
      select.focus({ preventScroll: true });
    }
  });
}

function openCountryDialog() {
  const component = getCountryDialogComponent();
  if (!component) return;

  const dialog = component.querySelector('dialog');
  const focusSelect = () => focusCountrySelect(component);

  if (dialog instanceof HTMLDialogElement && dialog.open) {
    focusSelect();
    return;
  }

  if (typeof component.showDialog === 'function') {
    component.addEventListener('dialog:open', focusSelect, { once: true });
    component.showDialog();
  } else if (dialog instanceof HTMLDialogElement) {
    dialog.showModal();
    focusSelect();
  }
}

function closeCountryDialog() {
  const component = getCountryDialogComponent();
  if (!component) return;

  if (typeof component.closeDialog === 'function') {
    component.closeDialog();
  } else {
    const dialog = component.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) {
      dialog.close();
    }
  }
}

/**
 * @param {HTMLElement} footer
 * @param {HTMLElement} footerRoot
 * @param {HTMLElement} panel
 */
function hideFooterPanelAfterTransition(footer, footerRoot, panel) {
  const finishClosing = () => {
    if (footer.dataset.expanded !== 'closing') return;

    panel.hidden = true;
    footer.dataset.expanded = 'false';
    delete footerRoot.dataset.maurtenFooterState;
  };

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    finishClosing();
    return;
  }

  /**
   * @param {TransitionEvent} event
   */
  const handleTransitionEnd = (event) => {
    if (event.target !== panel || event.propertyName !== 'max-height') return;

    panel.removeEventListener('transitionend', handleTransitionEnd);
    finishClosing();
  };

  panel.addEventListener('transitionend', handleTransitionEnd);
  window.setTimeout(() => {
    panel.removeEventListener('transitionend', handleTransitionEnd);
    finishClosing();
  }, 240);
}

document.addEventListener('click', (event) => {
  const clickedElement = event.target;
  if (!(clickedElement instanceof Element)) return;

  if (clickedElement.closest(NEWSLETTER_TRIGGER_SELECTOR)) {
    event.preventDefault();
    openNewsletter();
    return;
  }

  if (clickedElement.closest(NEWSLETTER_CLOSE_SELECTOR)) {
    event.preventDefault();
    closeNewsletter();
    return;
  }

  if (clickedElement.closest(COUNTRY_TRIGGER_SELECTOR)) {
    event.preventDefault();
    openCountryDialog();
    return;
  }

  if (clickedElement.closest(COUNTRY_CLOSE_SELECTOR)) {
    event.preventDefault();
    closeCountryDialog();
    return;
  }

  const toggle = clickedElement.closest(TOGGLE_SELECTOR);
  if (!(toggle instanceof HTMLButtonElement)) return;

  const footer = toggle.closest(FOOTER_SELECTOR);
  const panel = footer?.querySelector(PANEL_SELECTOR);
  const footerRoot = footer?.closest('.maurten-footer');
  if (!(footer instanceof HTMLElement) || !(footerRoot instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

  const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
  const nextExpanded = !isExpanded;

  toggle.setAttribute('aria-expanded', String(nextExpanded));

  if (nextExpanded) {
    panel.hidden = false;
    footerRoot.dataset.maurtenFooterState = 'expanded';
    requestAnimationFrame(() => {
      if (toggle.getAttribute('aria-expanded') !== 'true') return;

      footer.dataset.expanded = 'true';
    });
  } else {
    footer.dataset.expanded = 'closing';
    footerRoot.dataset.maurtenFooterState = 'closing';
    hideFooterPanelAfterTransition(footer, footerRoot, panel);
  }
});

document.addEventListener('change', (event) => {
  const changedElement = event.target;
  if (!(changedElement instanceof Element)) return;

  const select = changedElement.closest(COUNTRY_SELECT_SELECTOR);
  if (!(select instanceof HTMLSelectElement)) return;

  const form = select.form;
  if (form instanceof HTMLFormElement) {
    form.submit();
  }
});

// The customer form reloads the page on submit; reopen the dialog so the
// success or error message is visible. Wait for the dialog-component custom
// element to be defined before calling showDialog().
if (getNewsletterComponent()?.querySelector('[data-newsletter-posted]')) {
  customElements.whenDefined('dialog-component').then(openNewsletter);
}
